import type {ReplyPayload} from '@/types'

function arrayBufferToBase64(buffer: ArrayBufferLike): string {
    const bytes = new Uint8Array(buffer)
    let binary = ''
    const chunkSize = 0x8000
    for (let offset = 0; offset < bytes.length; offset += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize))
    }
    return btoa(binary)
}

function dataUrlToFile(dataUrl: string, fileName: string, mimeType: string): File {
    const comma = dataUrl.indexOf(',')
    const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl
    const binary = atob(b64)
    const bytes = new Uint8Array(binary.length)
    for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index)
    }
    return new File([bytes], fileName, {type: mimeType})
}

function downmixToMono(channelData: Float32Array[]): Float32Array {
    if (channelData.length === 1) return channelData[0]
    const length = channelData[0]?.length || 0
    const mono = new Float32Array(length)
    for (let sampleIndex = 0; sampleIndex < length; sampleIndex += 1) {
        let sum = 0
        for (const channel of channelData) {
            sum += channel[sampleIndex] || 0
        }
        mono[sampleIndex] = sum / channelData.length
    }
    return mono
}

function floatToS16le(samples: Float32Array): ArrayBuffer {
    const pcm = new Int16Array(samples.length)
    for (let index = 0; index < samples.length; index += 1) {
        const sample = Math.max(-1, Math.min(1, samples[index]))
        pcm[index] = sample < 0 ? sample * 0x8000 : sample * 0x7fff
    }
    return pcm.buffer
}

function resampleLinear(samples: Float32Array, sourceRate: number, targetRate: number): Float32Array {
    if (sourceRate === targetRate || samples.length === 0) return samples
    const outputLength = Math.max(1, Math.round(samples.length * targetRate / sourceRate))
    const output = new Float32Array(outputLength)
    const ratio = sourceRate / targetRate
    for (let index = 0; index < outputLength; index += 1) {
        const sourceIndex = index * ratio
        const left = Math.floor(sourceIndex)
        const right = Math.min(samples.length - 1, left + 1)
        const fraction = sourceIndex - left
        output[index] = samples[left] + (samples[right] - samples[left]) * fraction
    }
    return output
}

async function decodeAudioToPcm(buffer: ArrayBuffer): Promise<{pcm: ArrayBuffer; sampleRate: number}> {
    const AudioContextCtor =
        window.AudioContext ||
        (window as Window & typeof globalThis & {webkitAudioContext?: typeof AudioContext}).webkitAudioContext
    if (!AudioContextCtor) {
        throw new Error('当前浏览器不支持语音编码')
    }
    const ctx = new AudioContextCtor()
    try {
        const audio = await ctx.decodeAudioData(buffer.slice(0))
        const channels = Array.from({length: audio.numberOfChannels}, (_, index) => audio.getChannelData(index))
        const sampleRate = 16000
        const samples = resampleLinear(downmixToMono(channels), audio.sampleRate, sampleRate)
        return {
            pcm: floatToS16le(samples),
            sampleRate,
        }
    } finally {
        void ctx.close()
    }
}

function isWav(buffer: ArrayBuffer): boolean {
    if (buffer.byteLength < 12) return false
    const bytes = new Uint8Array(buffer, 0, 12)
    return (
        String.fromCharCode(...bytes.subarray(0, 4)) === 'RIFF' &&
        String.fromCharCode(...bytes.subarray(8, 12)) === 'WAVE'
    )
}

export async function encodeVoiceFileForWechat(file: File): Promise<{
    base64: string
    mimeType: string
    fileName: string
    fileSize: number
    voiceFormat: number
}> {
    const {encode} = await import('silk-wasm')
    const input = await file.arrayBuffer()
    let encoded
    try {
        const {pcm, sampleRate} = await decodeAudioToPcm(input)
        encoded = await encode(pcm, sampleRate)
    } catch (error) {
        if (!isWav(input)) throw error
        encoded = await encode(input, 0)
    }
    const data = encoded.data
    if (!data.byteLength) {
        throw new Error('语音编码失败')
    }
    const baseName = file.name.replace(/\.[^.]+$/, '') || `voice-${Date.now()}`
    const buffer = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength)
    return {
        base64: `data:audio/silk;base64,${arrayBufferToBase64(buffer)}`,
        mimeType: 'audio/silk',
        fileName: `${baseName}.silk`,
        fileSize: data.byteLength,
        voiceFormat: 4,
    }
}

export async function encodeReplyVoicePartForWechat(part: ReplyPayload): Promise<ReplyPayload> {
    if (part.voiceFormat === 4 && part.mimeType === 'audio/silk') return part
    const rawBase64 = part.rawBase64 || part.previewBase64 || part.base64
    if (!rawBase64) return part
    const sourceFile = dataUrlToFile(
        rawBase64,
        part.fileName || `voice-${Date.now()}.wav`,
        rawBase64.match(/^data:([^;,]+)/)?.[1] || part.mimeType || 'audio/wav',
    )
    const encoded = await encodeVoiceFileForWechat(sourceFile)
    return {
        ...part,
        base64: encoded.base64,
        fileName: encoded.fileName,
        fileSize: encoded.fileSize,
        mimeType: encoded.mimeType,
        voiceFormat: encoded.voiceFormat,
    }
}
